import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Clear";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min.js";
import BidTimer from "../../components/bid/BidTimer.jsx";
import NoBidsImage from "../../components/bid/NoBidsImage";
import StarsSelectInput from "../../components/fundamentals/StarsSelectInput.jsx";
import { BID_STATUS } from "../../util/bid/bidConstants.js";
import { sortOnCaseSensitiveTexts } from "../../util/commonUtils.js";
import { FEATURES } from "../../util/constants";
import { BID_SORT_BY_TYPES } from "../../util/bid/bidConstants";

import { getShiftDate } from "../../util/dateTimeFormatter";
import { LAST_UPDATED_TIMESTAMP } from "../../util/messageConstants.js";
import { getStationTimeZoneDateTimeStringFromUTCInteger } from "../../util/timeZoneService";

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: "24px",
    textAlign: "left",
    width: "210px",
    margin: "auto",
    marginTop: "10px",
    marginLeft: "20px",
    whiteSpace: "normal",
  },
  searchInput: {
    width: "47vw",
    right: "5px",
  },
  sortInput: {
    width: "47vw",
    left: "5px",
  },
  bidListItem: {
    margin: "10px",
    padding: "10px",
    textAlign: "left",
    background: theme.palette.secondary.swaGray1,
  },
  lastSubmitted: {
    fontSize: "12px",
  },
  status: {
    color: theme.palette.primary.swaBlue,
  },
}));

const MyBidsPageMobile = (props) => {
  const classes = useStyles();
  const history = useHistory();

  const SearchButton = () => {
    return (
      <IconButton
        size="small"
        className="search-icon-button"
        onMouseDown={props.onSearchSubmit.bind(this, props.searchString)}
      >
        <SearchIcon />
      </IconButton>
    );
  };

  const CancelIconButton = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={props.onSearchCancel.bind(this)}>
        <CancelIcon />
      </IconButton>
    );
  };

  const renderSearchBar = () => {
    return (
      <FormControl className={classes.searchInput} fullWidth>
        <TextField
          label="Search"
          variant="outlined"
          value={props.searchString}
          onChange={props.onSearchChange.bind(this)}
          onBlur={props.onSearchBlur.bind(this)}
          InputProps={{
            endAdornment:
              props.searchString !== "" ? (
                <InputAdornment position="end">
                  <CancelIconButton onSearchCancel={props.onSearchCancel} />
                  <SearchButton onSearchSubmit={props.onSearchSubmit} searchString={props.searchString} />
                </InputAdornment>
              ) : (
                <InputAdornment position="end">
                  <SearchButton />
                </InputAdornment>
              ),
          }}
          onKeyUp={props.onSearchEnterKeyPressed.bind(this)}
          error={props.searchStringErrors !== ""}
          helperText={props.searchStringErrors}
        />
      </FormControl>
    );
  };

  const getBidName = (bid) => {
    return bid
      ? `${bid?.station} | ${bid?.workgroup} | ${getShiftDate(bid?.schedStartDate)} - ${getShiftDate(
          bid?.schedEndDate
        )} ${bid?.bidType}`
      : "";
  };
  const getLastUpdatedTimestamp = (bidData) => {
    return bidData?.createdAt ? Date.parse(bidData.createdAt) : "";
  };

  const handleOnBidChoiceEventClick = (bid) => {
    if (bid.status === BID_STATUS.AWARDED) {
      props.setSelectedAwardedBidChoiceData(bid);
    } else if (bid.status !== BID_STATUS.WITHDRAWN && bid.status !== BID_STATUS.DELETED) {
      const bidChildItems = props.featureConfig.childItems;
      const bidChoicesFeature = bidChildItems.find((item) => item.key === FEATURES.BID_CHOICES);
      history.push({
        pathname: bidChoicesFeature.path.replace(":referenceNumber", bid.referenceNumber).replace(":eid", bid.eid),
      });
    }
  };

  const renderEmployeeBids = () => {
    return (
      <div>
        {props.bidList?.map((bid) => {
          return (
            <Paper
              key={bid?.id}
              className={classes.bidListItem}
              elevation={1}
              onClick={() => handleOnBidChoiceEventClick(bid)}
              data-testid={bid?.id}
            >
              <div>
                <div style={{ fontSize: "16px", fontWeight: "bold" }}>{getBidName(bid)}</div>
                <Typography component="div" align="left" className={classes.lastSubmitted}>
                  <b className={classes.status}>{getLastUpdatedTimestamp(bid) !== "" && bid.status}</b>
                  {getLastUpdatedTimestamp(bid) !== "" &&
                    ` | ${LAST_UPDATED_TIMESTAMP}${getStationTimeZoneDateTimeStringFromUTCInteger(
                      getLastUpdatedTimestamp(bid),
                      bid.station
                    ).toFormat("MM/dd/yyyy, T ZZZZ")}
                  `}
                </Typography>
                {bid.status !== BID_STATUS.WITHDRAWN &&
                  bid.status !== BID_STATUS.AWARDED &&
                  bid.status !== BID_STATUS.CLOSED && (
                    <Typography variant="body2" sx={{ color: "primary.swaRed", fontSize: "16px" }}>
                      <Grid
                        container
                        alignItems={"center"}
                        alignContent={"center"}
                        style={{ fontSize: "12px", wordWrap: "break-word" }}
                        spacing={0.2}
                        fontWeight={"bold"}
                        marginTop={"5px"}
                      >
                        <Grid item>
                          <AccessTimeIcon fontSize="12px" style={{ marginTop: "4px" }} />
                        </Grid>
                        <Grid item xs={11.2} sm={11.2}>
                          <BidTimer windowDates={[bid.windowStartDate, bid.windowEndDate]} station={bid.station} />
                        </Grid>
                      </Grid>
                    </Typography>
                  )}
              </div>
            </Paper>
          );
        })}
      </div>
    );
  };

  return (
    <div className="employee-my-bids-filter-form">
      <Grid container justifyContent="center" spacing={1}>
        <Grid container className="mobile-title-row">
          <Typography component={"div"}>
            <Box className={classes.title}>{"My Bids"}</Box>
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Box mt={1} mb={1}>
            <FormControl variant="outlined">
              <StarsSelectInput
                className={classes.sortInput}
                label="Sort By"
                id="employeeMyBidsFormSortBySelectElement"
                menuMessage="Sort By"
                value={props.mobileSortByValue}
                allOptions={Object.values(BID_SORT_BY_TYPES)}
                sortComparator={sortOnCaseSensitiveTexts}
                onChange={props.handleMobileSortByChange}
                errors={props.mobileSortByValue === "" ? "Default Sort By Value is missing" : ""}
              />
            </FormControl>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box mt={1} mb={1}>
            {renderSearchBar()}
          </Box>
        </Grid>
      </Grid>
      <Grid container>
        <Box
          sx={{
            bgcolor: "secondary.swaLightestBlue",
            width: "100%",
            height: "60px",
            borderTopLeftRadius: "5px",
            borderTopRightRadius: "5px",
            borderTop: "solid 1px",
            borderLeft: "solid 1px",
            borderRight: "solid 1px",
            borderColor: "secondary.swaGray2",
            borderRadius: 0,
          }}
        >
          <Stack
            sx={{ height: "100%", justifyContent: "left", alignItems: "center", padding: "15px" }}
            direction="row"
            divider={<Divider sx={{ marginTop: "5px", marginBottom: "5px" }} orientation="vertical" flexItem />}
          >
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={7} sm={7}></Grid>
              <Grid item xs={5} sm={5}>
                <Button
                  style={{ backgroundColor: "white", paddingLeft: 8, paddingRight: 8 }}
                  size="small"
                  fullWidth
                  onClick={props.onClickMobileFilterDialogOpen.bind(this)}
                  sx={{ border: "solid 0.5px", borderColor: "secondary.swaBlue" }}
                >
                  <Grid container spacing={1} justifyContent="space-between" alignItems="center" alignContent="center">
                    <Grid item alignItems="center" xs={3} sm={2} md={1}>
                      <FilterAltIcon sx={{ marginTop: 1, fontSize: "18px" }} />
                    </Grid>
                    <Grid item xs={9} sm={10} md={11}>
                      <Typography sx={{ fontSize: "14px" }}>
                        {props.modifiedFiltersCount > 0
                          ? `${props.modifiedFiltersCount} Filter${props.modifiedFiltersCount > 1 ? "s" : ""}`
                          : "Filters"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      </Grid>
      <div ref={props.bidTableMobileRef} className="enable-scroll">
        {props?.isLoading ? (
          <CircularProgress />
        ) : props?.bidList?.length === 0 ? (
          <NoBidsImage message="No Bids" />
        ) : (
          renderEmployeeBids()
        )}
      </div>
    </div>
  );
};

MyBidsPageMobile.propTypes = {
  bidList: PropTypes.array.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  searchString: PropTypes.string.isRequired,
  searchStringErrors: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchBlur: PropTypes.func.isRequired,
  onSearchCancel: PropTypes.func.isRequired,
  onSearchEnterKeyPressed: PropTypes.func.isRequired,
  onClickMobileFilterDialogOpen: PropTypes.func.isRequired,
  handleMobileSortByChange: PropTypes.func.isRequired,
  featureConfig: PropTypes.object.isRequired,
  bidTableMobileRef: PropTypes.object.isRequired,
  modifiedFiltersCount: PropTypes.number,
  mobileSortByValue: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  setSelectedAwardedBidChoiceData: PropTypes.func.isRequired,
};

export default MyBidsPageMobile;
